import { useState } from 'react';

import { formatDistanceToNow } from 'date-fns';
import { Mail, RefreshCw, X, ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { WorkspaceInvitationResponse } from '@optidev/shared-types';

interface PendingInvitationsProps {
  invitations: WorkspaceInvitationResponse[];
  onRevoke: (invitationId: string) => Promise<void>;
  onResend: (invitationId: string) => Promise<void>;
}

const roleBadgeColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200',
  user: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
  viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200',
};

/**
 * Pending Invitations Component
 * Collapsible section showing pending workspace invitations
 */
export function PendingInvitations({
  invitations,
  onRevoke,
  onResend,
}: PendingInvitationsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const { isMobile } = useResponsive();

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId);
    try {
      await onResend(invitationId);
    } finally {
      setResendingId(null);
    }
  };

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-4">
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Pending Invitations ({invitations.length})
            </span>
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="divide-y divide-border/50">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className={cn(
                  "flex gap-3 p-4",
                  isMobile ? "flex-col" : "items-center justify-between"
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{invitation.email}</span>
                      <Badge className={roleBadgeColors[invitation.role]}>
                        {invitation.role.charAt(0).toUpperCase() +
                          invitation.role.slice(1)}
                      </Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Invited{' '}
                      {formatDistanceToNow(new Date(invitation.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>

                <div className={cn(
                  "flex items-center gap-2",
                  isMobile && "justify-end"
                )}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResend(invitation.id)}
                    disabled={resendingId === invitation.id}
                  >
                    <RefreshCw
                      className={cn(
                        "mr-2 h-4 w-4",
                        resendingId === invitation.id && "animate-spin"
                      )}
                    />
                    Resend
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRevokeTarget(invitation.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={() => setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this invitation? The invite link
              will no longer work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (revokeTarget) {
                  onRevoke(revokeTarget);
                  setRevokeTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
